/**
 * SHER-462 — Excel/CSV export for the surgical waiting list ("Liste chirurgicale").
 *
 * Surgeons use the exported table as a record of surgical activity, so the CIM-11
 * diagnosis must be exported IN FULL — never the truncated "Arthrose du genou…"
 * cell shown in the table. The file is a UTF-8 CSV (opens directly in Excel); the
 * client has no xlsx library and this reuses the existing blob-download pattern
 * (see BillablesConfiguration.tsx) with zero new dependencies.
 */

export interface SurgicalListRow {
    nhc: string;
    patient: string;
    dob: string;
    mainSurgeon: string;
    department: string;
    service: string;
    /** Full, untruncated CIM-11 diagnosis (formatSurgeryCim11 output, "-" when none). */
    cim11Full: string;
    dateAdded: string;
    dateCompleted: string;
    totalDays: string;
    status: string;
}

export interface SurgicalListLabels {
    nhc: string;
    patient: string;
    dob: string;
    mainSurgeon: string;
    department: string;
    service: string;
    cim11: string;
    dateAdded: string;
    dateCompleted: string;
    totalDays: string;
    status: string;
}

/** RFC-4180 style cell escaping: quote cells containing comma/quote/newline. */
function escapeCsvCell(value: string): string {
    const cell = value ?? '';
    return /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}

/**
 * Build the surgical-list CSV. Patient identity columns (health id, name, DOB)
 * are included only for the global waiting list view (`includePatientColumns`),
 * mirroring the table which splices them in when not scoped to a single patient.
 */
export function buildSurgicalListCsv(
    rows: SurgicalListRow[],
    labels: SurgicalListLabels,
    includePatientColumns: boolean,
): string {
    const patientColumns: Array<[string, (row: SurgicalListRow) => string]> = [
        [labels.nhc, (row) => row.nhc],
        [labels.patient, (row) => row.patient],
        [labels.dob, (row) => row.dob],
    ];
    const clinicalColumns: Array<[string, (row: SurgicalListRow) => string]> = [
        [labels.mainSurgeon, (row) => row.mainSurgeon],
        [labels.department, (row) => row.department],
        [labels.service, (row) => row.service],
        [labels.cim11, (row) => row.cim11Full],
        [labels.dateAdded, (row) => row.dateAdded],
        [labels.dateCompleted, (row) => row.dateCompleted],
        [labels.totalDays, (row) => row.totalDays],
        [labels.status, (row) => row.status],
    ];
    const columns = includePatientColumns ? [...patientColumns, ...clinicalColumns] : clinicalColumns;

    const headerLine = columns.map(([header]) => escapeCsvCell(header)).join(',');
    const dataLines = rows.map((row) => columns.map(([, get]) => escapeCsvCell(get(row))).join(','));

    // Prepend a UTF-8 BOM so Excel reads accented French diagnoses correctly.
    return '\uFEFF' + [headerLine, ...dataLines].join('\r\n');
}

/** Trigger a browser download of a CSV string (reuses the repo's blob-anchor pattern). */
export function downloadCsv(filename: string, csv: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
}
